import { lazy, ReactNode, Suspense, useMemo, useSyncExternalStore } from 'react';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { setArticlePremiumPerksRenderer } from '../../../../../shared/frontend/libs/articlePremiumPerksRenderer';
import { TRouter } from '../../../../../shared/frontend/libs/router/types';

const ArticlePremiumPerks = lazy(() =>
  import('../components/presentational/articlePremiumPerks').then(({ ArticlePremiumPerks: Component }) => ({
    default: Component
  }))
);

type TArticleInjectionRouterProps = {
  children: ReactNode;
  router: TRouter;
};

/** Bridges the host router into a React root that `articleHelper` mounts outside the app tree. */
export function ArticleInjectionRouter({ children, router }: TArticleInjectionRouterProps) {
  const routerState = useSyncExternalStore(
    (onStoreChange) => router.subscribe(() => onStoreChange()),
    () => router.state,
    () => router.state
  );
  const navigator = useMemo(
    () => ({
      createHref: (to: any) => router.createHref(to),
      encodeLocation: (to: any) => router.encodeLocation(to),
      go: (delta: number) => void router.navigate(delta),
      push: (to: any, state: any, options: any) =>
        void router.navigate(to, { state, preventScrollReset: options?.preventScrollReset }),
      replace: (to: any, state: any, options: any) =>
        void router.navigate(to, { replace: true, state, preventScrollReset: options?.preventScrollReset })
    }),
    [router]
  );

  return (
    <Router location={routerState.location} navigationType={routerState.historyAction} navigator={navigator}>
      {children}
    </Router>
  );
}

export function registerArticlePremiumPerksRenderer(store: any, router: TRouter) {
  setArticlePremiumPerksRenderer(() => (
    <Provider store={store}>
      <ArticleInjectionRouter router={router}>
        <Suspense fallback={null}>
          <ArticlePremiumPerks />
        </Suspense>
      </ArticleInjectionRouter>
    </Provider>
  ));
}
